import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';

const AdminVenueForm = () => {
  const navigate = useNavigate();
  const { venueId } = useParams();
  const isEdit = Boolean(venueId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [venueClosedDatesMap, setVenueClosedDatesMap] = useState({});
  const [venuePricingRulesMap, setVenuePricingRulesMap] = useState({});
  const [pricingRules, setPricingRules] = useState([]);
  const [globalClosedDates, setGlobalClosedDates] = useState([]);
  const [globalClosedInput, setGlobalClosedInput] = useState('');
  const prevOperationalHoursRef = useRef({ open: 8, close: 22 });
  const didInitOperationalHoursRef = useRef(false);

  const [formData, setFormData] = useState({
    name: '',
    type: 'Lapangan A',
    description: '',
    image_url: '',
    open_hour: 8,
    close_hour: 22,
    is_closed: false,
    closed_dates_input: '',
  });

  const parseRules = (raw) => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map((r) => ({
        day_type: r?.day_type ?? r?.dayType ?? 'all',
        start_hour: r?.start_hour ?? r?.startHour ?? '',
        end_hour: r?.end_hour ?? r?.endHour ?? '',
        price_per_hour: r?.price_per_hour ?? r?.pricePerHour ?? '',
      }));
    } catch {
      return [];
    }
  };

  const parseDateList = (raw) => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((d) => typeof d === 'string' && d.trim()).map((d) => d.trim());
    } catch {
      return [];
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    if (!token || !userStr) {
      navigate('/admin/login');
      return;
    }
    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'admin') {
        navigate('/admin/login');
        return;
      }
    } catch {
      navigate('/admin/login');
      return;
    }

    loadAll();
  }, [navigate, venueId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const settingsResp = await api.get('/settings/public');
      const raw = settingsResp.data?.venue_closed_dates;
      const rawPricingMap = settingsResp.data?.venue_hourly_pricing_rules;
      let map = {};
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') map = parsed;
        } catch {}
      }
      setVenueClosedDatesMap(map);
      let pricingMap = {};
      if (rawPricingMap) {
        try {
          const parsed = JSON.parse(rawPricingMap);
          if (parsed && typeof parsed === 'object') pricingMap = parsed;
        } catch {}
      }
      setVenuePricingRulesMap(pricingMap);
      const currentVenueRules = isEdit ? pricingMap?.[venueId] : null;
      const nextRules = Array.isArray(currentVenueRules) ? parseRules(JSON.stringify(currentVenueRules)) : [];
      setPricingRules(nextRules.filter((r) => String(r?.day_type || 'all').toLowerCase() !== 'holiday'));
      setGlobalClosedDates(parseDateList(settingsResp.data?.global_closed_dates));

      if (isEdit) {
        const venueResp = await api.get(`/venues/${venueId}`);
        const venue = venueResp.data;
        const closedDates = Array.isArray(map?.[venueId]) ? map[venueId] : [];
        setFormData({
          name: venue.name || '',
          type: venue.type || 'Lapangan A',
          description: venue.description || '',
          image_url: venue.image_url || '',
          open_hour: venue.open_hour ?? 8,
          close_hour: venue.close_hour ?? 22,
          is_closed: Boolean(venue.is_closed),
          closed_dates_input: closedDates.join(', '),
        });
      }
    } catch (error) {
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    const nextOpen = Number(formData.open_hour);
    const nextClose = Number(formData.close_hour);
    if (!Number.isInteger(nextOpen) || !Number.isInteger(nextClose)) return;
    if (!didInitOperationalHoursRef.current) {
      prevOperationalHoursRef.current = { open: nextOpen, close: nextClose };
      didInitOperationalHoursRef.current = true;
      return;
    }
    const prevOpen = prevOperationalHoursRef.current.open;
    const prevClose = prevOperationalHoursRef.current.close;
    if (nextOpen === prevOpen && nextClose === prevClose) return;

    setPricingRules((prev) =>
      prev.map((r) => {
        const startHour = Number(r?.start_hour);
        const endHour = Number(r?.end_hour);
        if (Number.isInteger(startHour) && Number.isInteger(endHour) && startHour === prevOpen && endHour === prevClose) {
          return { ...r, start_hour: String(nextOpen), end_hour: String(nextClose) };
        }
        return r;
      })
    );

    prevOperationalHoursRef.current = { open: nextOpen, close: nextClose };
  }, [formData.open_hour, formData.close_hour, loading]);

  const normalizeClosedDates = (raw) => {
    const parts = String(raw || '')
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const valid = parts.filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
    return Array.from(new Set(valid)).sort();
  };

  const validationError = useMemo(() => {
    const openHour = Number(formData.open_hour);
    const closeHour = Number(formData.close_hour);
    if (!Number.isInteger(openHour) || openHour < 0 || openHour > 23) return 'Jam buka harus 0-23';
    if (!Number.isInteger(closeHour) || closeHour < 1 || closeHour > 24) return 'Jam tutup harus 1-24';
    if (closeHour <= openHour) return 'Jam tutup harus lebih besar dari jam buka';
    const closedDates = normalizeClosedDates(formData.closed_dates_input);
    for (let i = 0; i < closedDates.length; i++) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(closedDates[i])) return `Tanggal tutup tidak valid: ${closedDates[i]}`;
    }
    return '';
  }, [formData.open_hour, formData.close_hour, formData.closed_dates_input]);

  const settingsValidationError = useMemo(() => {
    for (let i = 0; i < pricingRules.length; i++) {
      const dayType = String(pricingRules[i].day_type || 'all').toLowerCase();
      const startHour = Number(pricingRules[i].start_hour);
      const endHour = Number(pricingRules[i].end_hour);
      const pricePerHour = Number(pricingRules[i].price_per_hour);
      if (!['all', 'weekday', 'weekend'].includes(dayType)) return `Baris ${i + 1}: day_type tidak valid`;
      if (!Number.isInteger(startHour) || startHour < 0 || startHour > 23) return `Baris ${i + 1}: start_hour harus 0-23`;
      if (!Number.isInteger(endHour) || endHour < 1 || endHour > 24) return `Baris ${i + 1}: end_hour harus 1-24`;
      if (startHour >= endHour) return `Baris ${i + 1}: start_hour harus lebih kecil dari end_hour`;
      if (!Number.isFinite(pricePerHour) || pricePerHour < 0) return `Baris ${i + 1}: price_per_hour harus angka >= 0`;
    }
    for (let i = 0; i < globalClosedDates.length; i++) {
      const v = globalClosedDates[i];
      if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return `Tanggal tutup global tidak valid: ${v}`;
    }

    const openHour = Number(formData.open_hour);
    const closeHour = Number(formData.close_hour);
    const resolveRate = (hour, dayType) => {
      const day = String(dayType || 'all').toLowerCase();
      for (const rule of pricingRules) {
        const startHour = Number(rule?.start_hour);
        const endHour = Number(rule?.end_hour);
        const pricePerHour = Number(rule?.price_per_hour);
        const ruleDayType = String(rule?.day_type || 'all').toLowerCase();
        if (!Number.isInteger(startHour) || !Number.isInteger(endHour)) continue;
        if (!Number.isFinite(pricePerHour) || pricePerHour <= 0) continue;
        if (ruleDayType === day && hour >= startHour && hour < endHour) return pricePerHour;
      }
      for (const rule of pricingRules) {
        const startHour = Number(rule?.start_hour);
        const endHour = Number(rule?.end_hour);
        const pricePerHour = Number(rule?.price_per_hour);
        const ruleDayType = String(rule?.day_type || 'all').toLowerCase();
        if (!Number.isInteger(startHour) || !Number.isInteger(endHour)) continue;
        if (!Number.isFinite(pricePerHour) || pricePerHour <= 0) continue;
        if (ruleDayType === 'all' && hour >= startHour && hour < endHour) return pricePerHour;
      }
      return null;
    };

    if (Number.isInteger(openHour) && Number.isInteger(closeHour) && closeHour > openHour) {
      const dayTypes = ['weekday', 'weekend'];
      for (let h = openHour; h < closeHour; h++) {
        for (const dt of dayTypes) {
          const rate = resolveRate(h, dt);
          if (!rate) return `Aturan harga belum mencakup jam ${String(h).padStart(2, '0')}:00 untuk ${dt}`;
        }
      }
    }

    return '';
  }, [pricingRules, globalClosedDates, formData.open_hour, formData.close_hour]);

  const updateRule = (index, patch) => {
    setPricingRules((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addRule = () => {
    const openHour = Number(formData.open_hour);
    const closeHour = Number(formData.close_hour);
    setPricingRules((prev) => [
      ...prev,
      {
        day_type: 'all',
        start_hour: Number.isInteger(openHour) ? String(openHour) : '',
        end_hour: Number.isInteger(closeHour) ? String(closeHour) : '',
        price_per_hour: '',
      },
    ]);
  };

  const removeRule = (index) => {
    setPricingRules((prev) => prev.filter((_, i) => i !== index));
  };

  const addGlobalClosedDate = () => {
    const date = String(globalClosedInput || '').trim();
    if (!date) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      toast.error('Format tanggal harus YYYY-MM-DD');
      return;
    }
    setGlobalClosedDates((prev) => Array.from(new Set([...prev, date])).sort());
    setGlobalClosedInput('');
  };

  const removeGlobalClosedDate = (date) => {
    setGlobalClosedDates((prev) => prev.filter((d) => d !== date));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    if (settingsValidationError) {
      toast.error(settingsValidationError);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        type: formData.type,
        price_per_hour: 0,
        description: formData.description,
        image_url: formData.image_url,
        open_hour: Number(formData.open_hour),
        close_hour: Number(formData.close_hour),
        is_closed: Boolean(formData.is_closed),
      };

      let savedId = venueId;
      if (isEdit) {
        const resp = await api.put(`/admin/venues/${venueId}`, payload);
        savedId = resp.data?.id || venueId;
        toast.success('Lapangan berhasil diupdate');
      } else {
        const resp = await api.post('/admin/venues', payload);
        savedId = resp.data?.id;
        toast.success('Lapangan berhasil ditambahkan');
      }

      const closedDates = normalizeClosedDates(formData.closed_dates_input);
      if (savedId) {
        const nextMap = { ...(venueClosedDatesMap || {}) };
        nextMap[savedId] = closedDates;
        await api.post('/settings', [{ key: 'venue_closed_dates', value: JSON.stringify(nextMap) }]);
        setVenueClosedDatesMap(nextMap);
      }

      if (savedId) {
        const normalizedRules = pricingRules
          .map((r) => ({
            day_type: String(r.day_type || 'all').toLowerCase(),
            start_hour: Number(r.start_hour),
            end_hour: Number(r.end_hour),
            price_per_hour: Number(r.price_per_hour),
          }))
          .filter((r) => r.day_type !== 'holiday')
          .sort((a, b) => a.start_hour - b.start_hour);

        const nextPricingMap = { ...(venuePricingRulesMap || {}) };
        nextPricingMap[savedId] = normalizedRules;

        await api.post('/settings', [
          { key: 'venue_hourly_pricing_rules', value: JSON.stringify(nextPricingMap) },
          { key: 'global_closed_dates', value: JSON.stringify(globalClosedDates) },
        ]);
        setVenuePricingRulesMap(nextPricingMap);
      }

      navigate('/admin/venues');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan lapangan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <Link to="/admin/venues" className="text-zinc-400 hover:text-white transition-colors">
                <ArrowLeft className="w-6 h-6" />
              </Link>
              <span className="text-xl font-heading font-bold italic text-white uppercase">
                {isEdit ? 'Edit Lapangan' : 'Tambah Lapangan'}
              </span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-zinc-900 border border-zinc-800 rounded-lg p-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {validationError && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                  {validationError}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Nama Lapangan</label>
                <input
                  data-testid="venue-name-input"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 placeholder:text-zinc-600 transition-all outline-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-zinc-300 mb-2">Jam Buka (0-23)</label>
                  <input
                    type="number"
                    min={0}
                    max={23}
                    value={formData.open_hour}
                    onChange={(e) => setFormData({ ...formData, open_hour: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 transition-all outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-zinc-300 mb-2">Jam Tutup (1-24)</label>
                  <input
                    type="number"
                    min={1}
                    max={24}
                    value={formData.close_hour}
                    onChange={(e) => setFormData({ ...formData, close_hour: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 transition-all outline-none"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  id="venue-is-closed"
                  type="checkbox"
                  checked={Boolean(formData.is_closed)}
                  onChange={(e) => setFormData({ ...formData, is_closed: e.target.checked })}
                  className="h-4 w-4 accent-primary"
                />
                <label htmlFor="venue-is-closed" className="text-sm font-semibold text-zinc-300">
                  Tutup sementara (semua tanggal)
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Tanggal Tutup (opsional)</label>
                <textarea
                  value={formData.closed_dates_input}
                  onChange={(e) => setFormData({ ...formData, closed_dates_input: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md px-4 py-3 placeholder:text-zinc-600 transition-all outline-none resize-none"
                  rows={2}
                  placeholder="Contoh: 2026-04-10, 2026-04-11"
                />
                <div className="mt-1 text-xs text-zinc-500">Format: YYYY-MM-DD. Pisahkan dengan koma atau spasi.</div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Deskripsi</label>
                <textarea
                  data-testid="venue-description-input"
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md px-4 py-3 placeholder:text-zinc-600 transition-all outline-none resize-none"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">URL Gambar (Opsional)</label>
                <input
                  data-testid="venue-image-input"
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 placeholder:text-zinc-600 transition-all outline-none"
                />
              </div>

              <div className="flex gap-4 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/admin/venues')}
                  className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  Batal
                </Button>
                <button
                  data-testid="submit-venue-btn"
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-primary text-black hover:bg-primary-hover px-6 py-2 rounded-md font-bold uppercase transition-all disabled:opacity-50"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <Save className="w-4 h-4" />
                    {saving ? 'Menyimpan...' : 'Simpan'}
                  </span>
                </button>
              </div>
            </form>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10 bg-zinc-900 border border-zinc-800 rounded-lg p-8">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div>
              <div className="text-2xl font-heading font-bold uppercase text-white">Harga</div>
              <div className="text-sm text-zinc-400">Atur harga weekday/weekend dan tanggal tutup.</div>
            </div>
          </div>

          {settingsValidationError && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {settingsValidationError}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-zinc-800">
              <thead className="bg-black">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-zinc-400 uppercase tracking-wider">Hari</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-zinc-400 uppercase tracking-wider">Start</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-zinc-400 uppercase tracking-wider">End</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-zinc-400 uppercase tracking-wider">Harga / Jam</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-zinc-400 uppercase tracking-wider">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {pricingRules.map((rule, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3">
                      <select
                        value={String(rule.day_type || 'all').toLowerCase()}
                        onChange={(e) => updateRule(idx, { day_type: e.target.value })}
                        className="w-36 bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white focus:outline-none focus:border-primary"
                      >
                        <option value="all">Semua</option>
                        <option value="weekday">Weekday</option>
                        <option value="weekend">Weekend</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min={0}
                        max={23}
                        value={rule.start_hour}
                        onChange={(e) => updateRule(idx, { start_hour: e.target.value })}
                        className="w-24 bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white focus:outline-none focus:border-primary"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min={1}
                        max={24}
                        value={rule.end_hour}
                        onChange={(e) => updateRule(idx, { end_hour: e.target.value })}
                        className="w-24 bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white focus:outline-none focus:border-primary"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min={0}
                        value={rule.price_per_hour}
                        onChange={(e) => updateRule(idx, { price_per_hour: e.target.value })}
                        className="w-52 bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white focus:outline-none focus:border-primary"
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button type="button" variant="destructive" size="sm" onClick={() => removeRule(idx)} disabled={saving}>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Hapus
                      </Button>
                    </td>
                  </tr>
                ))}

                {pricingRules.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-sm text-zinc-500">
                      Belum ada aturan.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center mt-6">
            <Button type="button" variant="outline" onClick={addRule} disabled={saving}>
              <Plus className="w-4 h-4 mr-2" />
              Tambah Aturan
            </Button>
            <div className="text-xs text-zinc-500">end tidak termasuk (start &lt;= jam &lt; end).</div>
          </div>

          <div className="mt-8 border-t border-zinc-800 pt-6">
            <div className="text-sm font-bold text-white uppercase mb-3">Tanggal Tutup Global</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="date"
                value={globalClosedInput}
                onChange={(e) => setGlobalClosedInput(e.target.value)}
                className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white focus:outline-none focus:border-primary"
              />
              <Button type="button" variant="outline" onClick={addGlobalClosedDate} disabled={saving}>
                <Plus className="w-4 h-4 mr-2" />
                Tambah Tanggal
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {globalClosedDates.length === 0 ? (
                <div className="text-sm text-zinc-500">Belum ada tanggal tutup global.</div>
              ) : (
                globalClosedDates.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => removeGlobalClosedDate(d)}
                    className="px-3 py-1 rounded-full text-xs font-bold uppercase border border-zinc-700 text-zinc-300 hover:border-red-500/40 hover:text-red-400 transition-colors"
                  >
                    {d}
                  </button>
                ))
              )}
            </div>
            <div className="mt-2 text-xs text-zinc-500">Jika tanggal masuk list ini, booking akan non-aktif untuk semua lapangan.</div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminVenueForm;
